import React, { useEffect, useMemo, useState } from 'react';
import api, { authService } from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';
import ScmAssigneePicker from './ScmAssigneePicker';
import Pagination from './Pagination';
import './UserManagement.css';

const PAGE_SIZE = 12;

/**
 * Only this domain may hold a staff role. The directory also contains
 * @eyantraventures.com and other addresses; they are valid people but must not
 * get access here. Enforced server-side too — this is just so the mistake is
 * caught before a round-trip.
 */
const STAFF_EMAIL_DOMAIN = '@eyantra.com';

/** Window for counting someone as recently active. */
const ACTIVE_WINDOW_DAYS = 30;

/** Relative "last seen", short enough for a table cell. */
function relativeTime(iso) {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;

    const days = Math.floor((Date.now() - then) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days} days ago`;
    if (days < 60) return 'last month';
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return 'over a year ago';
}

/**
 * Readable name from an eYantra address. Older accounts were created by hand
 * with a username like "Siva", so we key off email — which every account has —
 * rather than username.
 */
function displayNameFromEmail(email) {
    const local = String(email || '').split('@')[0];
    if (!local) return email || '—';
    return local
        .split(/[._-]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function initialsFor(name) {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ROLE_HINTS = {
    ADMIN: 'Full access, including Brandstore and team access.',
    CURATOR: 'Manages catalogue, approvals, gallery and bookings.',
    EMPLOYEE: 'Builds curated carts and works event leads.',
    CUSTOMER: 'No staff access — storefront only.',
};

export default function UserManagement() {
    const { user: currentUser } = useAuth();
    const confirm = useConfirm();

    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [status, setStatus] = useState('loading'); // loading | ready | error
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('ALL');
    const [page, setPage] = useState(0);

    const [savingId, setSavingId] = useState(null);
    const [banner, setBanner] = useState(null); // { kind: 'error' | 'success', text }

    // -1 = directory unreachable, null = not answered yet. Kept distinct so the
    // tile can show "—" rather than implying eYantra has no employees.
    const [directoryTotal, setDirectoryTotal] = useState(null);

    const [grantEmail, setGrantEmail] = useState('');
    const [grantRole, setGrantRole] = useState('EMPLOYEE');
    const [granting, setGranting] = useState(false);

    const load = () => {
        setStatus('loading');
        Promise.all([
            authService.get('auth/admin/users'),
            authService.get('auth/admin/roles'),
        ])
            .then(([usersRes, rolesRes]) => {
                setUsers(usersRes.data || []);
                setRoles(rolesRes.data || []);
                setStatus('ready');
            })
            .catch(err => {
                // Distinguish "couldn't load" from "nobody has access" — an empty
                // table on failure reads as real data, and wrong data is worse
                // than a visible error.
                console.error('Failed to load team access:', err);
                setStatus('error');
            });
    };

    useEffect(load, []);

    // Directory headcount comes from ecom-backend (Graph), not auth-service, and
    // is deliberately not part of `load` — a Graph outage should leave the roster
    // perfectly usable rather than failing the whole screen.
    useEffect(() => {
        let cancelled = false;
        api.get('/users/count')
            .then(res => { if (!cancelled) setDirectoryTotal(res.data?.total ?? -1); })
            .catch(err => {
                console.error('Failed to load directory headcount:', err);
                if (!cancelled) setDirectoryTotal(-1);
            });
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return users
            .filter(u => roleFilter === 'ALL' || u.role === roleFilter)
            .filter(u => !q
                || String(u.email || '').toLowerCase().includes(q)
                || String(u.username || '').toLowerCase().includes(q)
                || displayNameFromEmail(u.email).toLowerCase().includes(q));
    }, [users, search, roleFilter]);

    const adminCount = useMemo(
        () => users.filter(u => u.role === 'ADMIN').length,
        [users]
    );

    const activeCount = useMemo(() => {
        const cutoff = Date.now() - ACTIVE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
        return users.filter(u => u.lastLoginAt && new Date(u.lastLoginAt).getTime() >= cutoff).length;
    }, [users]);

    // Login tracking was added after these accounts existed, so a null lastLoginAt
    // means "not seen since tracking began", not "never signed in". Say so while
    // that's still most of the table, rather than presenting a misleadingly low number.
    const trackingIsYoung = useMemo(
        () => users.length > 0 && users.every(u => !u.lastLoginAt),
        [users]
    );

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const visible = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

    const onFilterChange = (fn) => (e) => { fn(e.target.value); setPage(0); };

    const isSelf = (u) =>
        currentUser
        && (u.username === currentUser.username
            || String(u.email || '').toLowerCase() === String(currentUser.email || '').toLowerCase());

    const changeRole = async (target, nextRole) => {
        if (nextRole === target.role) return;

        const name = displayNameFromEmail(target.email);
        const notes = [];
        if (nextRole === 'ADMIN') {
            notes.push('Admins can manage Brandstore, approvals and everyone’s access.');
        }
        if (isSelf(target) && target.role === 'ADMIN') {
            notes.push('This is your own account — you will lose admin access immediately.');
        }
        if (nextRole === 'CUSTOMER') {
            notes.push('They keep their account but lose all staff access.');
        }

        const losingAccess = nextRole === 'CUSTOMER' || (isSelf(target) && target.role === 'ADMIN');
        const ok = await confirm({
            title: `Change ${name} from ${target.role} to ${nextRole}?`,
            message: notes.join('\n\n'),
            confirmLabel: `Make ${nextRole}`,
            tone: losingAccess ? 'danger' : 'default',
        });
        if (!ok) return;

        setSavingId(target.userId);
        setBanner(null);
        try {
            const res = await authService.patch(
                `auth/admin/users/${target.userId}/role`,
                { role: nextRole }
            );
            setUsers(prev => prev.map(u => (u.userId === target.userId ? res.data : u)));
            setBanner({ kind: 'success', text: `${name} is now ${nextRole}.` });
        } catch (err) {
            // 409 carries the last-admin guard; show the server's reason rather
            // than a generic failure, because it tells the admin what to do.
            const reason = err.response?.data?.error || 'Could not change that role. Try again.';
            console.error('Role change failed:', err);
            setBanner({ kind: 'error', text: reason });
        } finally {
            setSavingId(null);
        }
    };

    const grant = async (e) => {
        e.preventDefault();
        const email = grantEmail.trim().toLowerCase();
        if (!email) return;

        if (!email.endsWith(STAFF_EMAIL_DOMAIN)) {
            setBanner({
                kind: 'error',
                text: `Only ${STAFF_EMAIL_DOMAIN} accounts can be given access. ${email} is outside that domain.`,
            });
            return;
        }

        setGranting(true);
        setBanner(null);
        try {
            const res = await authService.post('auth/admin/users/grant', { email, role: grantRole });
            setUsers(prev => {
                const without = prev.filter(u => u.userId !== res.data.userId);
                return [...without, res.data];
            });
            setGrantEmail('');
            setBanner({
                kind: 'success',
                text: `${displayNameFromEmail(email)} will sign in as ${grantRole}.`,
            });
        } catch (err) {
            const reason = err.response?.data?.error || 'Could not grant access. Try again.';
            console.error('Grant access failed:', err);
            setBanner({ kind: 'error', text: reason });
        } finally {
            setGranting(false);
        }
    };

    if (status === 'loading') {
        return <div className="um-state">Loading team access…</div>;
    }

    if (status === 'error') {
        return (
            <div className="um-state um-state--error">
                <strong>Couldn’t load the team list.</strong>
                <p>The auth service didn’t respond. This isn’t the same as nobody having access.</p>
                <button className="um__retry" onClick={load}>Try again</button>
            </div>
        );
    }

    return (
        <div className="um">
            <header className="um__header">
                <div>
                    <h2 className="um__title">Team Access</h2>
                    <p className="um__subtitle">
                        Everyone at eYantra who can sign in, and what they can do. Anyone with
                        an <code>@eyantra.com</code> Microsoft account is added as EMPLOYEE the
                        first time they sign in — use this screen to raise or remove that.
                    </p>
                </div>
            </header>

            {/* Stat tiles, not a chart: three scalars with no series and no time
                axis. Values use proportional figures — tabular-nums is for columns. */}
            <dl className="um__stats">
                <div className="um__stat">
                    <dt className="um__stat-label">With access</dt>
                    <dd className="um__stat-value">{users.length}</dd>
                    <dd className="um__stat-note">hold a staff role</dd>
                </div>
                <div className="um__stat">
                    <dt className="um__stat-label">At eYantra</dt>
                    <dd className="um__stat-value">
                        {directoryTotal === null ? '…' : directoryTotal < 0 ? '—' : directoryTotal}
                    </dd>
                    <dd className="um__stat-note">
                        {directoryTotal === null
                            ? 'checking directory'
                            : directoryTotal < 0
                                ? 'directory unavailable'
                                : `${STAFF_EMAIL_DOMAIN} in Azure AD`}
                    </dd>
                </div>
                <div className="um__stat">
                    <dt className="um__stat-label">Signed in</dt>
                    <dd className="um__stat-value">{trackingIsYoung ? '—' : activeCount}</dd>
                    <dd className="um__stat-note">
                        {trackingIsYoung
                            ? 'tracking just started'
                            : `in the last ${ACTIVE_WINDOW_DAYS} days`}
                    </dd>
                </div>
            </dl>

            {banner && (
                <div className={`um__banner um__banner--${banner.kind}`} role="status">
                    {banner.text}
                </div>
            )}

            <form className="um__grant" onSubmit={grant}>
                <div className="um__grant-field">
                    <label className="um__grant-label" htmlFor="um-grant-person">
                        Give someone access
                    </label>
                    <ScmAssigneePicker
                        value={grantEmail}
                        onChange={setGrantEmail}
                        disabled={granting}
                        emailDomain={STAFF_EMAIL_DOMAIN}
                        placeholder={`Search eYantra directory (${STAFF_EMAIL_DOMAIN} only)`}
                    />
                </div>
                <label className="um__grant-role">
                    <span className="um__grant-label">Role</span>
                    <select value={grantRole} onChange={e => setGrantRole(e.target.value)} disabled={granting}>
                        {roles.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </label>
                <button type="submit" className="um__grant-btn" disabled={granting || !grantEmail.trim()}>
                    {granting ? 'Saving…' : 'Grant access'}
                </button>
            </form>

            <div className="um__controls">
                <input
                    type="search"
                    className="um__search"
                    placeholder="Search by name or email…"
                    value={search}
                    onChange={onFilterChange(setSearch)}
                    aria-label="Search team members"
                />
                <label className="um__role-filter">
                    <span className="um__role-filter-label">Role</span>
                    <select value={roleFilter} onChange={onFilterChange(setRoleFilter)}>
                        <option value="ALL">All roles</option>
                        {Array.from(new Set(users.map(u => u.role))).sort().map(r => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                </label>
            </div>

            {filtered.length === 0 ? (
                <div className="um-state">
                    Nobody matches that search. Clear the filters to see everyone with access.
                </div>
            ) : (
                <>
                    <table className="um__table">
                        <thead>
                            <tr>
                                <th scope="col">Person</th>
                                <th scope="col">Method</th>
                                <th scope="col">Last seen</th>
                                <th scope="col" title="Sign-in events since tracking began — not a measure of how much someone uses the platform.">
                                    Sign-ins
                                </th>
                                <th scope="col">Role</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map(u => {
                                const name = displayNameFromEmail(u.email);
                                const lastAdmin = u.role === 'ADMIN' && adminCount <= 1;
                                return (
                                    <tr key={u.userId}>
                                        <td>
                                            <div className="um__person">
                                                <span className="um__avatar" aria-hidden="true">{initialsFor(name)}</span>
                                                <span className="um__person-text">
                                                    <span className="um__person-name">
                                                        {name}
                                                        {isSelf(u) && <span className="um__you">you</span>}
                                                    </span>
                                                    <span className="um__person-email">{u.email}</span>
                                                </span>
                                            </div>
                                        </td>
                                        <td className="um__signin">
                                            {u.ssoLinked ? 'Microsoft SSO' : 'Password'}
                                        </td>
                                        <td className="um__signin">
                                            {relativeTime(u.lastLoginAt)
                                                // Null is "not seen since tracking began", which is
                                                // not the same as never — say so rather than showing
                                                // a dash that reads as "never signed in".
                                                || <span className="um__untracked" title="This account has not signed in since login tracking was added.">not yet tracked</span>}
                                        </td>
                                        <td className="um__signin um__signins-count">
                                            {u.loginCount ?? '—'}
                                        </td>
                                        <td>
                                            <select
                                                className={`um__role-select um__role-select--${u.role.toLowerCase()}`}
                                                value={u.role}
                                                disabled={savingId === u.userId || lastAdmin}
                                                title={lastAdmin
                                                    ? 'The last admin’s role can’t be changed — promote someone else first.'
                                                    : ROLE_HINTS[u.role]}
                                                onChange={e => changeRole(u, e.target.value)}
                                            >
                                                {roles.map(r => <option key={r} value={r}>{r}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <Pagination
                            currentPage={safePage}
                            totalPages={totalPages}
                            onPageChange={setPage}
                        />
                    )}
                </>
            )}
        </div>
    );
}
